// VER72-F3 (docs/build-briefs/VER72-F3-pay-label-after-line-change.md): the rule for when the payment
// preview no longer describes the ticket. Pure, no DOM, so a unit test can state it.
//
// The preview is asked about a set of ticket lines. It describes the ticket only while the lines now on
// the ticket are exactly those lines with exactly the choices that set their price. A line added,
// removed, voided or changed (liquor, pour size, modifiers) since the read was started means the
// preview's amount belongs to a ticket that no longer exists.
//
// The signature deliberately uses the INPUTS to a line's price, never the quoted total
// (lineTotalCents). The quote arrives from its own request, on its own schedule, and sets the total
// without any further preview read; keying on it would leave Pay stuck at the bare word after a quote
// landed. Every edit that changes an input ends in a preview read, which is what clears the staleness.

export interface PreviewLineInputs {
  orderLineId: string;
  liquorProductId?: string | undefined;
  ounces?: string | undefined;
  pourModifierApplied?: boolean | undefined;
  modifierSelections?: readonly string[] | undefined;
}

/** What the preview read was asked about, for one line: its id and every choice that sets its price. */
export function lineSignature(line: PreviewLineInputs): string {
  return JSON.stringify([
    line.orderLineId,
    line.liquorProductId ?? null,
    line.ounces ?? null,
    line.pourModifierApplied === true,
    [...(line.modifierSelections ?? [])].sort(),
  ]);
}

/** True when the lines now on the ticket differ in any way from the lines the last preview read was
 * asked about: one added, one gone, or one with a different price input. An empty ticket against a
 * preview asked about no lines is NOT changed (nothing differs); the caller decides what an empty
 * ticket's label says. */
export function previewLinesChanged(
  askedSignatures: ReadonlySet<string>,
  currentLines: readonly PreviewLineInputs[],
): boolean {
  if (currentLines.length !== askedSignatures.size) {
    return true;
  }
  return currentLines.some((line) => !askedSignatures.has(lineSignature(line)));
}
